import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from '@mui/material';
import { SearchField } from '@/shared/ui/search/AdvancedSearch/types/field.types';
import { useAdvancedSearchTheme } from '@/shared/ui/search/AdvancedSearch/theme/useAdvancedSearchTheme';
import { FilterValue } from '@/shared/ui/search/AdvancedSearch/types/filter.types';
import { CascadeSelect } from './CascadeSelect';
import { SearchableSelect } from './SearchableSelect';

interface IProps {
  field: SearchField;
  value: FilterValue;
  onChange: (value: FilterValue) => void;
}

export function ValueInput({ field, value, onChange }: IProps) {
  const { valueInputSx } = useAdvancedSearchTheme();

  if (field.searchableConfig) {
    return (
      <SearchableSelect
        field={field}
        value={value}
        onChange={onChange}
        multiple={field.type === 'multiselect'}
      />
    );
  }

  switch (field.type) {
    case 'text':
    case 'number':
      return renderTextInput(field, value, onChange, valueInputSx);
    case 'date':
      return renderDateInput(field, value, onChange, valueInputSx);
    case 'datetime':
      return renderDateTimeInput(field, value, onChange, valueInputSx);
    case 'select':
      if (field.options) return renderSelectInput(field, value, onChange, valueInputSx);
      break;
    case 'multiselect':
      if (field.options) return renderMultiSelectInput(field, value, onChange, valueInputSx);
      break;
    case 'boolean':
      return renderBooleanInput(value, onChange, valueInputSx);
    case 'cascade':
      return <CascadeSelect field={field} value={value} onChange={onChange} />;
    default:
      break;
  }

  return renderFallbackInput(value, onChange, valueInputSx);
}

function renderTextInput(
  field: SearchField,
  value: FilterValue,
  onChange: (value: FilterValue) => void,
  valueInputSx: any
) {
  return (
    <TextField
      size="small"
      type={field.type}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={field.placeholder || '값 입력'}
      sx={valueInputSx}
    />
  );
}

function renderDateInput(
  field: SearchField,
  value: FilterValue,
  onChange: (value: FilterValue) => void,
  valueInputSx: any
) {
  return (
    <TextField
      size="small"
      type="date"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      sx={valueInputSx}
      InputLabelProps={{ shrink: true }}
    />
  );
}

function renderDateTimeInput(
  field: SearchField,
  value: FilterValue,
  onChange: (value: FilterValue) => void,
  valueInputSx: any
) {
  return (
    <TextField
      size="small"
      type="datetime-local"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      sx={valueInputSx}
      InputLabelProps={{ shrink: true }}
    />
  );
}

function renderSelectInput(
  field: SearchField,
  value: FilterValue,
  onChange: (value: FilterValue) => void,
  valueInputSx: any
) {
  return (
    <FormControl sx={valueInputSx} size="small">
      <Select value={value || ''} onChange={(e) => onChange(e.target.value)}>
        {field.options?.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function renderMultiSelectInput(
  field: SearchField,
  value: FilterValue,
  onChange: (value: FilterValue) => void,
  valueInputSx: any
) {
  const selectedValues = Array.isArray(value) ? value : [];

  return (
    <FormControl sx={valueInputSx} size="small">
      <Select
        multiple
        value={selectedValues}
        onChange={(e) => onChange(e.target.value as (string | number)[])}
        input={<OutlinedInput />}
        renderValue={(selected) =>
          (selected as (string | number)[])
            .map((val) => field.options?.find((opt) => opt.value === val)?.label || val)
            .join(', ')
        }
      >
        {field.options?.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            <Checkbox checked={selectedValues.includes(opt.value)} />
            <ListItemText primary={opt.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function renderBooleanInput(
  value: FilterValue,
  onChange: (value: FilterValue) => void,
  valueInputSx: any
) {
  return (
    <FormControl sx={valueInputSx} size="small">
      <Select
        value={value === null || value === undefined ? '' : value.toString()}
        onChange={(e) => onChange(e.target.value === 'true')}
      >
        <MenuItem value="true">예</MenuItem>
        <MenuItem value="false">아니오</MenuItem>
      </Select>
    </FormControl>
  );
}

function renderFallbackInput(
  value: FilterValue,
  onChange: (value: FilterValue) => void,
  valueInputSx: any
) {
  return (
    <TextField
      size="small"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder="값 입력"
      sx={valueInputSx}
    />
  );
}
